// AI Spec System — API client.
//
// 方案 X 后：
//   - 公开 GET 用于文档页 /ai/spec
//   - detect-endpoint / suggest-mapping 用于服务商在 /register 表单里实时调
//   - admin endpoints 用于 /admin/ai/catalog 和 /admin/ai/pending
//
// 旧的 wizard provider/endpoint/model 系列 API 已删除（合并进 marketplace）。

import { XApi } from '../services.js';
import { tokens as authTokens } from './tokens.js';

const cfg = () => ({ apiBase: XApi.config.apiBase });

async function plainFetch(method, path, body) {
  const url = cfg().apiBase + path;
  const init = { method, credentials: 'include', headers: { 'Content-Type': 'application/json' } };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await fetch(url, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${method} ${path} → ${res.status}: ${text || res.statusText}`);
  }
  return res.status === 204 ? null : res.json();
}

async function authedFetch(method, path, body) {
  let token = authTokens.getAccess();
  if (!token) {
    try { token = await XApi.helpers.refreshAccessToken(); } catch { /* normalized below */ }
  }
  if (!token) throw new Error('not authenticated');
  const url = cfg().apiBase + path;
  const init = {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  let res = await fetch(url, init);
  if (res.status === 401) {
    token = await XApi.helpers.refreshAccessToken();
    init.headers.Authorization = `Bearer ${token}`;
    res = await fetch(url, init);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(`${method} ${path} → ${res.status}: ${text || res.statusText}`);
    err.status = res.status;
    throw err;
  }
  return res.status === 204 ? null : res.json();
}

// ── Public ──────────────────────────────────────────────────────────────────

// C2: 单进程缓存。catalog 一天才变一次，没必要每次进 AI Spec tab 都拉。
//     注册流 + admin 页 + ai-spec 公开页都共用这个 cache。
let _canonicalCache = null;
let _canonicalPromise = null;
const _CANONICAL_TTL_MS = 5 * 60 * 1000;

/** 列出所有可用 canonical 模型（带 providerCount）。带 5min cache。 */
export async function listCanonicalModelsPublic({ force = false } = {}) {
  const now = Date.now();
  if (!force && _canonicalCache && now - _canonicalCache.at < _CANONICAL_TTL_MS) {
    return _canonicalCache.data;
  }
  if (_canonicalPromise) return _canonicalPromise;
  _canonicalPromise = plainFetch('GET', '/v1/ai-spec/canonical-models')
    .then((data) => {
      _canonicalCache = { data, at: Date.now() };
      _canonicalPromise = null;
      return data;
    })
    .catch((e) => {
      _canonicalPromise = null;
      throw e;
    });
  return _canonicalPromise;
}

/** 让 admin 改完 canonical 后强制下次重新拉。 */
export function invalidateCanonicalCache() {
  _canonicalCache = null;
}

// Interface specs registry — 跟 canonical 同样的 process-wide cache 策略。
// spec 集合只在后端发新版本时变，5min TTL 足够。
let _interfaceSpecsCache = null;
let _interfaceSpecsPromise = null;

/** 列出后端注册的 interface specs（前端下拉用，避免写死）。 */
export async function listInterfaceSpecsPublic({ force = false } = {}) {
  const now = Date.now();
  if (!force && _interfaceSpecsCache && now - _interfaceSpecsCache.at < _CANONICAL_TTL_MS) {
    return _interfaceSpecsCache.data;
  }
  if (_interfaceSpecsPromise) return _interfaceSpecsPromise;
  _interfaceSpecsPromise = plainFetch('GET', '/v1/ai-spec/interface-specs')
    .then((data) => {
      _interfaceSpecsCache = { data, at: Date.now() };
      _interfaceSpecsPromise = null;
      return data;
    })
    .catch((e) => {
      _interfaceSpecsPromise = null;
      throw e;
    });
  return _interfaceSpecsPromise;
}

/** 公开：列出哪些 active service 提供某个 canonical（C1 钻取用）。 */
export async function listProvidersForCanonical(canonicalId) {
  return plainFetch('GET', `/v1/ai-spec/canonical-models/${encodeURIComponent(canonicalId)}/providers`);
}

// ── Service provider helpers（在 /register 表单里实时调）────────────────────
/**
 * 探测一个 endpoint path 是否是 AI 协议。
 * 返回 { pathMatch, billingHint, suggested } —— 见 plan §9.2。
 */
export async function detectEndpoint(path, billingType) {
  return authedFetch('POST', '/v1/ai-spec/detect-endpoint', { path, billingType });
}

/**
 * 对一批上游模型名做 fuzzy 匹配，返每个的候选 canonical 列表。
 */
export async function suggestMapping(upstreamNames) {
  return authedFetch('POST', '/v1/ai-spec/suggest-mapping', { upstreamNames });
}

// ── Admin: pending queue ────────────────────────────────────────────────────
export async function listPending() {
  return authedFetch('GET', '/admin/ai/pending');
}
/** 处理一条 pending：把指定 (endpointId, modelName) 配上 canonicalId（或 null 重置） */
export async function resolvePending(id, canonicalId) {
  return authedFetch('PATCH', `/admin/ai/pending/${encodeURIComponent(id)}`, { canonicalId });
}

// ── Admin: canonical catalog ────────────────────────────────────────────────
export async function listCanonicalAdmin() {
  return authedFetch('GET', '/admin/ai/canonical');
}
export async function createCanonical(input) {
  const r = await authedFetch('POST', '/admin/ai/canonical', input);
  invalidateCanonicalCache();
  return r;
}
export async function updateCanonical(id, patch) {
  const r = await authedFetch('PATCH', `/admin/ai/canonical/${id}`, patch);
  invalidateCanonicalCache();
  return r;
}
